/**
 * Survey Says browser-source entrypoint.
 *
 * URL params:
 *   key  — API key (required for non-mock mode)
 *   ws   — WS port (default 3001) for local dev
 *   mock=1 — skip WS and run a scripted demo (Phase 2 enhancement; just
 *            renders the lobby for now)
 */

import type { OutMsg } from '@sg/shared';
import { readViewerMode, wsUrlFor } from '@sg/shared';
import { MOCK_ROWS } from './dev/mock-rows.js';
import { WsClient } from './ws/client.js';
import { resolveWsBase } from './ws/ws-base.js';
import { Store } from './state/store.js';
import type { SceneName } from './state/types.js';
import { createDomStage } from './scene/lib/stage.js';
import { applyTheme, DEFAULT_THEME } from './scene/lib/theme.js';
import { createLobby } from './scene/lobby.js';
import { createPrompt } from './scene/prompt.js';
import { createBoard } from './scene/board.js';
import { createResults } from './scene/results.js';
import { createPodium } from './scene/podium.js';
import { createConnectionLost } from './scene/connection-lost.js';
import { audioBus } from './audio/bus.js';
import { sfx as sfxImpl } from './audio/sfx.js';
import { music as musicImpl } from './audio/music.js';
import { mountFullscreenButton } from './ui/fullscreen-button.js';
import { mountAudioUnlock } from './ui/audio-unlock.js';
import { createSparkles } from './ui/sparkles.js';
import { createSideLeaderboard } from './ui/side-leaderboard.js';
import { createCountdown } from './ui/countdown.js';
import { createGuessBubbles } from './ui/guess-bubbles.js';
import { createConflictBanner } from './ui/conflict-banner.js';
import { mountWatchQrWidget } from '../../../shared/qrcode-generator/watch-qr.js';

/** Slug of THIS browser source — used to filter `cross_game_conflict` events. */
const MY_SLUG = 'survey-says';

applyTheme(DEFAULT_THEME);

const params = new URL(window.location.href).searchParams;
const mockMode = params.get('mock') === '1';
// Viewer mode (`?viewer=1&handle=<HANDLE>`) routes through the public read-only
// /watch/<handle>/feed socket and suppresses streamer-only chrome.
const viewerMode = readViewerMode(params);
const isViewer = viewerMode.kind === 'viewer';
const key = viewerMode.kind === 'streamer' ? viewerMode.key : 'dev-no-key';
const wsPort = Number(params.get('ws') ?? '3001');

// Viewer-aware audio facades: in viewer mode every sfx/music call is a no-op
// so the AudioContext is never instantiated and Chromium never logs the
// "AudioContext was not allowed to start" warning.
const sfx: typeof sfxImpl = isViewer
  ? ({
      preload: async () => {},
      chime: () => {},
      ding: () => {},
      buzz: () => {},
      tick: () => {},
    } as unknown as typeof sfxImpl)
  : sfxImpl;
const music: typeof musicImpl = isViewer
  ? ({ play: () => {}, stop: () => {} } as unknown as typeof musicImpl)
  : musicImpl;

const root = document.getElementById('root')!;
root.innerHTML = '';
root.style.cssText = 'position: relative; width: 100vw; height: 100vh; overflow: hidden;';
root.setAttribute('data-testid', 'browser-root');

const stage = createDomStage({ mount: root });
const stageEl = stage.el;

const lobby = createLobby(stageEl);
const prompt = createPrompt(stageEl);
const board = createBoard(stageEl);
const results = createResults(stageEl);
const podium = createPodium(stageEl);
const connectionLost = createConnectionLost(stageEl);

// Polish overlays: side leaderboard during play, countdown widget top-left,
// sparkles canvas overlaying everything, fullscreen + audio-unlock chrome.
const sideLeaderboard = createSideLeaderboard(stageEl);
const countdown = createCountdown(stageEl);
const sparkles = createSparkles(stageEl);
const guessBubbles = createGuessBubbles(stageEl);
// Cross-game conflict banner — backend fans `cross_game_conflict` to every
// connected browser source for the tenant; we filter by MY_SLUG below.
const conflictBanner = createConflictBanner(stageEl);
sideLeaderboard.setVisible(false);
countdown.hide();
guessBubbles.setVisible(false);

if (!isViewer) {
  mountFullscreenButton();
  mountAudioUnlock();
  // Audio: pre-fetch + decode all SFX buffers so the first cue has no hitch.
  // Lobby bed starts immediately; if the bus is locked the HTMLAudio play()
  // promise rejects silently and music.resumeIfPending() picks it up later.
  void sfx.preload();
  music.play('lobby');
}

// Hide everything except lobby initially.
const allScenes: Record<SceneName, { setVisible(v: boolean): void }> = {
  lobby,
  prompt,
  board,
  results,
  podium,
  connection_lost: connectionLost,
};

function applyScene(next: SceneName): void {
  for (const [name, h] of Object.entries(allScenes)) {
    h.setVisible(name === next);
  }
  // Side LB visible during all in-game scenes (prompt/board/results), not lobby/podium/connection_lost.
  const inGame = next === 'prompt' || next === 'board' || next === 'results';
  sideLeaderboard.setVisible(inGame);
  if (!inGame) countdown.hide();
  // Guess bubbles only during the answer window (prompt scene). Clearing on
  // scene exit prevents stale bubbles from carrying into the reveal phase.
  guessBubbles.setVisible(next === 'prompt');
  // Music routing: lobby bed loops in lobby; in-game scenes (prompt/board/
  // results) loop a quieter round bed underneath SFX; podium triggers the
  // celebratory results bed. Stop music only on connection_lost.
  if (next === 'lobby') {
    music.play('lobby');
  } else if (next === 'podium') {
    music.play('results');
  } else if (next === 'connection_lost') {
    music.stop();
  } else {
    music.play('round');
  }
  root.setAttribute('data-scene', next);
  (window as unknown as { __ss_scene?: SceneName }).__ss_scene = next;
}

const store = new Store();
applyScene(store.getState().scene);

let lastRevealedSurveyId: string | null = null;
let lastAllTimeSig: string | null = null;
let lastSessionSig: string | null = null;
// Drives sfx.tick() once per second in the last 5s of the answer window.
let tickEndsAt = 0;
let lastTickSecond = -1;

let watchQrCtrl: ReturnType<typeof mountWatchQrWidget> | null = null;
let showWatchQr = true;
let currentHandle: string | null = null;

function applyWatchQrVisibility(handle: string | null): void {
  if (isViewer) return;
  if (!showWatchQr) {
    if (watchQrCtrl) { watchQrCtrl.destroy(); watchQrCtrl = null; }
    return;
  }
  if (watchQrCtrl) {
    watchQrCtrl.update(handle);
  } else {
    watchQrCtrl = mountWatchQrWidget({ handle });
  }
}

function handleWs(msg: OutMsg): void {
  store.dispatchWs(msg);
  // Side-effects: SFX cues, sparkles, countdown driving — anything that
  // doesn't belong in the pure reducer.
  switch (msg.type) {
    case 'state_snapshot': {
      const snapSettings = (msg.state as any).settings;
      const nextShow = snapSettings && typeof snapSettings.show_watch_qr === 'boolean' ? snapSettings.show_watch_qr : true;
      if (nextShow !== showWatchQr) {
        showWatchQr = nextShow;
        applyWatchQrVisibility(currentHandle);
      }
      // Side-leaderboard "ALL TIME" view: prefer the all-time list (up to 200
      // rows) when present, fall back to the top-10 (legacy snapshots from
      // older backends). Tiny signature compare avoids thrash.
      const rows = msg.state.leaderboard_all_time ?? msg.state.leaderboard_top10 ?? [];
      const sig = rows.map((r) => `${r.player_id}:${r.total_points}`).join('|');
      if (sig !== lastAllTimeSig) {
        sideLeaderboard.setAllTimeRows(rows);
        lastAllTimeSig = sig;
      }
      // Optimistic audio unlock; OBS page-permissions usually allow it. In
      // viewer mode the iframe is muted by design so we skip the unlock.
      if (!isViewer && !audioBus.isUnlocked()) void audioBus.unlock();
      break;
    }
    case 'ss_question_open':
      sfx.chime();
      lastRevealedSurveyId = msg.survey_id;
      // Drive countdown from question_open ends_at_ms (server-authoritative).
      // countdown_starts_at_ms (when set + future) holds the visible timer at
      // the full duration until the broadcast-relay grace passes, so viewers
      // see the FULL answer window on screen (not duration - delay).
      countdown.setPhase('Answer', msg.ends_at_ms, msg.countdown_starts_at_ms);
      tickEndsAt = msg.ends_at_ms;
      lastTickSecond = -1;
      sparkles.burst({ x: 960, y: 220, count: 18, speed: 0.8 });
      break;
    case 'ss_answer_reveal':
      if (lastRevealedSurveyId !== msg.survey_id) {
        lastRevealedSurveyId = msg.survey_id;
      }
      sfx.ding();
      // Reveals come AFTER the answer window closes; hide the countdown so
      // viewers know we're past the timed phase.
      countdown.hide();
      tickEndsAt = 0;
      // Fire a sparkle burst AT the revealed row's position (not screen
      // center). The board renders rows keyed by rank via
      // data-testid="ss-row-rank-{rank}"; we defer one frame so the store
      // subscriber's setQuestion() has appended the row before we measure.
      // Same pattern as Who Am I's letter-tile burst (commit 2fd716b).
      {
        const rank = msg.rank;
        requestAnimationFrame(() => {
          const row = document.querySelector<HTMLDivElement>(
            `[data-testid="ss-row-rank-${rank}"]`,
          );
          if (!row) {
            // Fallback: a single small center burst if the row didn't render.
            sparkles.burst({ x: 590, y: 540, count: 22, speed: 0.85 });
            return;
          }
          const stageNode = document.getElementById('ss-stage');
          const stageRect = stageNode?.getBoundingClientRect();
          const stageScale =
            (window as unknown as { __ss_stage_scale?: number }).__ss_stage_scale ?? 1;
          const r = row.getBoundingClientRect();
          const cxPx = r.left + r.width / 2;
          const cyPx = r.top + r.height / 2;
          const x = stageRect ? (cxPx - stageRect.left) / stageScale : cxPx;
          const y = stageRect ? (cyPx - stageRect.top) / stageScale : cyPx;
          sparkles.burst({ x, y, count: 22, speed: 0.85 });
        });
      }
      break;
    case 'ss_question_results': {
      // Per-row dings already announced everything; if NO player matched any
      // surveyed answer, fire a short low buzz so viewers feel the whiff.
      if (msg.total_points_awarded === 0) sfx.buzz();
      countdown.hide();
      // Feed the per-game session leaderboard into the side panel. Reducer
      // already updated state.leaderboardSession; we mirror it into the
      // panel directly here so the cycle picks it up immediately.
      const session = msg.session_leaderboard ?? [];
      const sig = session.map((r) => `${r.player_id}:${r.total_points}`).join('|');
      if (sig !== lastSessionSig) {
        sideLeaderboard.setSessionRows(session);
        lastSessionSig = sig;
      }
      break;
    }
    case 'ss_game_over':
      sfx.chime();
      // Big confetti burst on the podium screen.
      sparkles.confetti({ count: 180 });
      countdown.hide();
      break;
    case 'ss_guess_received':
      // Visual feedback only — spawn a rising bubble with the guess text.
      // Backend rate-limits to ~10 frames/sec; the overlay additionally
      // soft-caps concurrent bubbles so the DOM stays bounded.
      guessBubbles.push(msg.guess);
      break;
    case 'cross_game_conflict':
      // Banner only matters for the slug being attempted OR the slug currently
      // running — other game scenes shouldn't surface the warning.
      if (msg.attempted_slug === MY_SLUG || msg.running_slug === MY_SLUG) {
        conflictBanner.show(msg.message);
      }
      break;
    case 'auth_ok' as OutMsg['type']: {
      const authMsg = msg as unknown as { type: 'auth_ok'; handle: string | null };
      currentHandle = authMsg.handle;
      applyWatchQrVisibility(authMsg.handle);
      break;
    }
    default:
      break;
  }
}

store.subscribe((next, prev) => {
  if (next.scene !== prev.scene) applyScene(next.scene);
  if (next.theme && next.theme !== prev.theme) applyTheme(next.theme);
  if (next.scene === 'lobby') {
    lobby.setLeaderboard(next.leaderboard, next.leaderboardAllTime);
  }
  if (next.scene === 'prompt' && next.question) {
    prompt.setQuestion(next.question);
  }
  if (next.scene === 'board' && next.question) {
    board.setQuestion(next.question);
  }
  if (next.scene === 'results' && next.results) {
    results.setResults(next.results);
  }
  if (next.scene === 'podium' && next.podium) {
    podium.setPodium(next.podium);
  }
});

// Drive the prompt countdown ring at 5Hz while the prompt scene is visible.
setInterval(() => {
  if (store.getState().scene === 'prompt') prompt.tick();
}, 200);

// Tick SFX driver: fires sfx.tick() once per second in the final 5 seconds of
// the answer window. tickEndsAt is set on ss_question_open and cleared on
// reveal/results. Cheap setInterval — checks once per 200ms which is enough
// resolution for a 1-second tick.
setInterval(() => {
  if (tickEndsAt <= 0) return;
  const remainingMs = tickEndsAt - Date.now();
  if (remainingMs <= 0 || remainingMs > 5_000) return;
  const secondsLeft = Math.ceil(remainingMs / 1000);
  if (secondsLeft !== lastTickSecond && secondsLeft >= 1 && secondsLeft <= 5) {
    lastTickSecond = secondsLeft;
    sfx.tick();
  }
}, 200);

// --- Connect ---
let wsClient: WsClient | null = null;
function connectWs(): void {
  // Viewer mode → /watch/<handle>/feed (read-only). Streamer mode keeps the
  // existing /browser?key=...&game=survey-says handshake.
  const wsUrl = wsUrlFor(viewerMode, resolveWsBase(wsPort), { game: 'survey-says' });
  wsClient = new WsClient({
    url: wsUrl,
    sessionToken: key,
    viewer: isViewer,
    onMessage: handleWs,
    onState: (s) => {
      store.dispatch({ type: 'connection', value: s });
      // The watch SPA owns the connection-lost UI for viewer iframes.
      if (!isViewer) connectionLost.setVisible(s !== 'open');
    },
  });
  wsClient.connect();
  (window as unknown as Record<string, unknown>).__ss_ws = wsClient;
}

if (mockMode) {
  // Phase 1 mock: just render lobby with a placeholder leaderboard so
  // streamers can preview the visual style without a live game.
  lobby.setLeaderboard(MOCK_ROWS, MOCK_ROWS);
  // Seed both views of the side leaderboard so the toggle has content
  // even in mock mode.
  sideLeaderboard.setAllTimeRows(MOCK_ROWS);
  sideLeaderboard.setSessionRows(MOCK_ROWS.slice(0, 5));
  // Optimistic audio unlock — works in OBS browser-permission contexts.
  void audioBus.unlock();
} else {
  connectWs();
}

// Auto-pause when browser source not visible (OBS scene switch / tab bg).
// Closes WS so the backend's soft-abort grace can end the game when no
// sources remain for this tenant+slug. Reconnects on revisible.
if (!mockMode) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (wsClient) {
        try { wsClient.close(); } catch { /* swallow */ }
        wsClient = null;
      }
    } else if (document.visibilityState === 'visible') {
      if (!wsClient) connectWs();
    }
  });
}

// Expose handler so screenshot harnesses can inject synthetic frames.
(window as unknown as Record<string, unknown>).__ss_handleWs = handleWs;

// First-gesture audio unlock — one click anywhere primes the bus. Viewer
// mode is muted by design (the watch SPA can ship its own unmute later).
if (!isViewer) {
  window.addEventListener(
    'pointerdown',
    () => {
      void audioBus.unlock();
    },
    { once: true },
  );
}

(window as unknown as Record<string, unknown>).__ss_store = store;
